/* eslint-disable no-param-reassign */
import { Dashboard, SvgIconComponent } from '@mui/icons-material';
import AltRoute from '@mui/icons-material/AltRoute';
import CreateOutlined from '@mui/icons-material/CreateOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import DirectionsBoatFilledOutlinedIcon from '@mui/icons-material/DirectionsBoatFilledOutlined';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import FeedbackOutlined from '@mui/icons-material/FeedbackOutlined';
import FmdGoodOutlined from '@mui/icons-material/FmdGoodOutlined';
import Fullscreen from '@mui/icons-material/Fullscreen';
import Handyman from '@mui/icons-material/Handyman';
import History from '@mui/icons-material/History';
import LockOutlined from '@mui/icons-material/LockOutlined';
import MapOutlined from '@mui/icons-material/MapOutlined';
import SaveOutlined from '@mui/icons-material/SaveOutlined';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import Correspondents from '../assets/icons/side-menu/correspondents.svg';
import FirstCall from '../assets/icons/side-menu/first-call.svg';
import IndustryNews from '../assets/icons/side-menu/industry-news.svg';
import Port from '../assets/icons/side-menu/port.svg';
import VPS from '../assets/icons/side-menu/vps.svg';

export enum EMenuItems {
  NOTHING_SELECTED = '',
  COUNTRIES = 'Countries',
  MAPS = 'Maps and Maritime Cartography',
  INCIDENTS = 'Incidents',
  BOUNDARIES = 'Boundaries',
  PORTS = 'Ports',
  ROUTES = 'Routes',
  DRAWINGS = 'Drawings',
  RI_MARITIME_AREAS = 'Risk Intelligence Maritime Areas',
  HISTORY = 'History',
  DOCUMENTS = 'Documents',
  SAVE_SETUP = 'Save Setup',
  PRIVACY_POLICY = 'Privacy Policy',
  TOOLS = 'Tools',
  INDUSTRY_NEWS = 'News',
  VPS_PORTS = 'VPS Ports',
  FIRST_CALL_PORTS = 'First Call Ports',
  CORRESPONDENTS = 'Correspondents',
  MY_FLEET = 'My Fleet',
  IRAN_CONFLICT = 'Iran Conflict',
}

export enum EDossiers {
  NOTHING = '',
  COUNTRY = 'Country',
  INCIDENT = 'Incident',
  HISTORY = 'History',
  PORT = 'Port',
  DRAWING = 'Drawing',
  INDUSTRY_NEWS = 'News',
  RI_MARITIME_AREA = 'RI Maritime Area',
  ROUTE = 'Route',
  VESSEL = 'Vessel',
  FIRST_CALL = 'First Call',
  CORRESPONDENTS = 'Correspondents',
  MY_FLEET = 'My Fleet',
}

export enum EMenuIcons {
  MAPS = 'MapOutlined',
  INCIDENTS = 'FmdGoodOutlined',
  BOUNDARIES = 'Fullscreen',
  PORTS = 'Port',
  ROUTES = 'AltRoute',
  DRAWINGS = 'CreateOutlined',
  RI_MARITIME_AREAS = 'Dashboard',
  HISTORY = 'History',
  DOCUMENTS = 'DescriptionOutlined',
  SAVE_SETUP = 'SaveOutlined',
  PRIVACY_POLICY = 'LockOutlined',
  FEEDBACK = 'FeedbackOutlined',
  TOOLS = 'Handyman',
  INDUSTRY_NEWS = 'IndustryNews',
  VPS_PORTS = 'VPS',
  FIRST_CALL_PORTS = 'FirstCall',
  CORRESPONDENTS = 'Correspondents',
  MY_FLEET = 'DirectionsBoatFilledOutlinedIcon',
  IRAN_CONFLICT = 'ErrorOutline',
}

type IconType = SvgIconComponent | string;

export const MenuIcons: Record<EMenuIcons, IconType> = {
  MapOutlined,
  FmdGoodOutlined,
  Fullscreen,
  AltRoute,
  CreateOutlined,
  Dashboard,
  History,
  SaveOutlined,
  LockOutlined,
  FeedbackOutlined,
  DescriptionOutlined,
  Handyman,
  VPS,
  Correspondents,
  IndustryNews,
  FirstCall,
  Port,
  DirectionsBoatFilledOutlinedIcon,
  ErrorOutline,
};

export const EMenuTitles = {
  INCIDENTS: 'Incidents',
  PORTS: 'Ports',
  VESSELS_AND_PORTS: 'Vessels and Ports',
  AREAS: 'Areas',
  OBSERVATIONS: 'Observations',
  MAP_AND_TOOLS: 'Maps & Tools',
  USER: 'User',
  MY_FLEET: 'My Fleet',
  FOCUS_REGIONS: 'Focus Regions',
};

export interface MenuState {
  menuOpen: boolean;
  selectedOption: EMenuItems;
  secondaryMenuOpen: boolean;
  selectedDossier: EDossiers;
  dossierHistory: { dossier: EDossiers; option: EMenuItems }[];
  threatAssessmentOpen: boolean;
  selectedAreaDocumentId: string;
  timelineMinimised: boolean;
}

export const INITIAL_MENU_STATE: MenuState = {
  menuOpen: false,
  selectedOption: EMenuItems.NOTHING_SELECTED,
  dossierHistory: [],
  secondaryMenuOpen: false,
  selectedDossier: EDossiers.NOTHING,
  threatAssessmentOpen: false,
  selectedAreaDocumentId: '',
  timelineMinimised: false,
};

const MenuSlice = createSlice({
  name: 'menu',
  initialState: INITIAL_MENU_STATE,
  reducers: {
    toggleMainMenu: (state) => {
      state.menuOpen = !state.menuOpen;
    },
    closeMenus: (state) => {
      state.menuOpen = false;
      state.secondaryMenuOpen = false;
      state.selectedOption = EMenuItems.NOTHING_SELECTED;
    },
    toggleSecondaryMenu: (state, action) => {
      if (action.payload) {
        state.secondaryMenuOpen = action.payload;
        state.menuOpen = false;
      } else {
        state.secondaryMenuOpen = action.payload;
      }
    },
    setSecondaryMenu: (state) => {
      state.secondaryMenuOpen = !state.secondaryMenuOpen;
    },
    setAllDossierClosed: (state) => {
      state.selectedDossier = EDossiers.NOTHING;
      state.dossierHistory = [];
      state.threatAssessmentOpen = false;
    },
    clearDossierHistory: (state) => {
      state.dossierHistory = [];
    },
    setAllAssessmentsClosed: (state) => {
      state.threatAssessmentOpen = false;
    },
    setSelectedOption: (state, action: PayloadAction<EMenuItems>) => {
      MenuSlice.caseReducers.setAllAssessmentsClosed(state);
      state.secondaryMenuOpen = true;
      state.selectedOption = action.payload;
    },
    setSelectedDossier: (state, action: PayloadAction<EDossiers>) => {
      state.dossierHistory = [
        ...state.dossierHistory,
        { dossier: state.selectedDossier, option: state.selectedOption },
      ];
      state.selectedDossier = action.payload;
    },
    setSelectedAreaDocument: (state, action: PayloadAction<string>) => {
      state.selectedAreaDocumentId = action.payload;
    },
    setThreatAssessmentOpen: (state, action: PayloadAction<boolean>) => {
      MenuSlice.caseReducers.setAllDossierClosed(state);
      state.threatAssessmentOpen = action.payload;
      state.selectedOption = EMenuItems.NOTHING_SELECTED;
    },
    setTimelineMinimised: (state, action: PayloadAction<boolean>) => {
      state.timelineMinimised = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function

export const {
  toggleMainMenu,
  closeMenus,
  toggleSecondaryMenu,
  setSecondaryMenu,
  setThreatAssessmentOpen,
  setSelectedOption,
  setSelectedDossier,
  setAllDossierClosed,
  setAllAssessmentsClosed,
  setSelectedAreaDocument,
  setTimelineMinimised,
} = MenuSlice.actions;

export default MenuSlice.reducer;
